/**
 * The Shipyard station panel (M6 spec §4, plan P27): the player-buyable hulls with price, standing requirement,
 * the fleet cap and the stock fit. Thin: the rules live in `models/shipyard.ts`. Keyboard and pad reach every
 * Buy button through the focus model (`FocusButton`; the station screen re-registers external buttons in DOM order).
 *
 * M10 P5a1a (hull tiers spec §6.3): the rows sit under band tabs (`shipyard-tab-<band>`; a band with no row has no
 * tab). The last tab picked is remembered for the session (module state, not saved). Each row shows the maker mark,
 * role glyph, band, length, the dock-mode line and the doctrine line (text placeholders for the marks, D-052).
 *
 * M10 P5a2a: a locked row lists its missing gate steps (`shipyard-gate-<hull>`) and offers **Pin goal**
 * (`shipyard-pin-<hull>`, `onPin`; one pin, a new one replaces it; the pinned row reads "Pinned"). `onCompare` is
 * the row slot P5a3a's compare panel fills (`shipyard-compare-<hull>`); absent, no button. Gates off and neither
 * callback passed: the markup is today's.
 *
 * M10 P5a3b: an optional name field (`shipyard-name`, 1 to 24 characters, trimmed) names the next ship bought; blank
 * = the registry's default name, and `onBuy` gets no name (today's purchase).
 */
import { useState, type ReactNode } from 'react'
import type { HullBand } from '../../strategic/hullLines'
import { FocusButton } from '../focus'
import { keepTyping } from '../corp/typing'
import { cleanShipName } from '../../fleet-link/hangar'
import { SHIP_NAME_MAX_CHARS } from '../../career/types'
import type { ShipyardModel, ShipyardTab } from './models/shipyard'
import '../strategic/strategic.css'
import './stationPanels.css'

export interface ShipyardProps {
  model: ShipyardModel
  status: string | null
  busy: boolean
  /** `name`: the trimmed name typed for the new ship; absent = the default name (M10 P5a3b). */
  onBuy: (hull: string, name?: string) => void
  /** M10 P5a2a: pin `hull` as the HUD goal (writes `Career.pinnedHull`); absent: no Pin button. */
  onPin?: (hull: string) => void
  /** M10 P5a2a: the row slot for P5a3a's compare panel; absent: no Compare button. */
  onCompare?: (hull: string) => void
  /** The tab to open on (tests and static renders); absent = the session's last tab, else the first. */
  tab?: HullBand
  /**
   * M10 P5a3c: the registry's default name for the next ship, shown in the empty name field (a placeholder, so a
   * blank field still buys unnamed: today's command). Absent: "Registry default".
   */
  defaultName?: string
}

/** The band tab picked last this session (null before the first pick). */
let lastTab: HullBand | null = null

/** The tab shown: `want` when it has rows, else the first tab (null when there are none). */
export function activeShipyardTab(tabs: readonly ShipyardTab[], want: HullBand | null): ShipyardTab | null {
  return tabs.find((t) => t.band === want) ?? tabs[0] ?? null
}

export function Shipyard(p: ShipyardProps): ReactNode {
  const m = p.model
  const [picked, setPicked] = useState<HullBand | null>(p.tab ?? lastTab)
  const [name, setName] = useState('')
  const typed = cleanShipName(name)
  const active = activeShipyardTab(m.tabs, picked)
  const rows = active?.rows ?? []
  return (
    <div className="sui fp-panel" data-testid="fleet-shipyard">
      <header className="sui-head">
        <h2 className="sui-title">{m.title}</h2>
        {m.subtitle !== '' ? <p className="sui-sub">{m.subtitle}</p> : null}
      </header>
      <p className="fp-meta">
        <span data-testid="shipyard-cap">{m.capLabel}</span>
        <span>{m.fundsLabel}</span>
      </p>
      {m.empty !== null ? <p className="sui-empty">{m.empty}</p> : null}
      {m.empty === null ? (
        <label className="fp-meta">
          Name the ship (optional)
          <input
            type="text"
            maxLength={SHIP_NAME_MAX_CHARS}
            value={name}
            placeholder={p.defaultName ?? 'Registry default'}
            disabled={p.busy}
            data-testid="shipyard-name"
            onKeyDown={keepTyping}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
      ) : null}
      {m.tabs.length > 0 ? (
        <nav className="ui-tabs" role="tablist">
          {m.tabs.map((t, i) => (
            <FocusButton
              key={t.band}
              group="shipyard-tab"
              index={i}
              testId={`shipyard-tab-${t.band}`}
              className={`ui-tab-btn${t.band === active?.band ? ' is-active' : ''}`}
              onActivate={() => {
                lastTab = t.band
                setPicked(t.band)
              }}
            >
              {t.label}
            </FocusButton>
          ))}
        </nav>
      ) : null}
      {rows.map((r, i) => (
        <div key={r.hull} className="fp-row" data-testid={`shipyard-row-${r.hull}`} {...(r.canBuy ? {} : { 'data-blocked': r.block ?? '' })}>
          <div>
            <div className="fp-row__name">
              {r.name} <span className="fp-row__sub">{r.classLabel}</span>
            </div>
            <div className="fp-row__sub" data-testid={`shipyard-readout-${r.hull}`}>
              {r.maker !== null ? <span data-testid={`shipyard-maker-${r.hull}`}>{r.maker}</span> : null}
              {r.roleGlyph !== null ? <span data-testid={`shipyard-role-${r.hull}`}> {`${r.roleGlyph} ${r.roleLabel ?? ''}`}</span> : null}
              <span data-testid={`shipyard-band-${r.hull}`}> · {r.band}</span>
              {r.lengthLabel !== null ? <span data-testid={`shipyard-length-${r.hull}`}> · {r.lengthLabel}</span> : null}
            </div>
            {r.dockLine !== null ? (
              <div className="fp-row__sub" data-testid={`shipyard-dock-${r.hull}`}>
                {r.dockLine}
              </div>
            ) : null}
            {r.doctrine !== null ? (
              <div className="fp-row__sub" data-testid={`shipyard-doctrine-${r.hull}`}>
                {r.doctrine}
              </div>
            ) : null}
            <div className="fp-row__sub">{r.specs}</div>
            {r.standingLabel !== null ? <div className="fp-row__sub">{r.standingLabel}</div> : null}
            <div className="fp-row__sub">{r.fitLabel}</div>
            {r.gateLines.length > 0 ? (
              <ul className="fp-row__sub" data-testid={`shipyard-gate-${r.hull}`}>
                {r.gateLines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="fp-row__price ui-num">{r.priceLabel}</div>
          {r.reason !== '' ? <div className="fp-row__reason">{r.reason}</div> : null}
          <div className="fp-row__actions">
            <FocusButton group="shipyard" index={i} className="ui-btn--small" testId={`shipyard-buy-${r.hull}`} disabled={!r.canBuy || p.busy} onActivate={() => (typed === null ? p.onBuy(r.hull) : p.onBuy(r.hull, typed))}>
              Buy {r.name}
            </FocusButton>
            {p.onPin !== undefined && (r.pinnable || r.pinned) ? (
              <FocusButton
                group="shipyard-pin"
                index={i}
                className="ui-btn--small"
                testId={`shipyard-pin-${r.hull}`}
                disabled={r.pinned || p.busy}
                onActivate={() => p.onPin?.(r.hull)}
              >
                {r.pinned ? 'Pinned' : 'Pin goal'}
              </FocusButton>
            ) : null}
            {p.onCompare !== undefined ? (
              <FocusButton group="shipyard-compare" index={i} className="ui-btn--small" testId={`shipyard-compare-${r.hull}`} onActivate={() => p.onCompare?.(r.hull)}>
                Compare
              </FocusButton>
            ) : null}
          </div>
        </div>
      ))}
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="shipyard-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
